import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function BestMuseumsInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-museums-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Vancouver Art Gallery",
    "section-2": "Museum of Anthropology",
    "section-3": "Science World",
    "section-4": "Museum of Vancouver",
    "section-5": "Vancouver Maritime Museum",
    "section-6": "Bill Reid Gallery",
    "section-7": "Beaty Biodiversity Museum",
    "section-8": "H.R. MacMillan Space Centre",
    "section-9": "Vancouver Police Museum",
    "section-10": "Roedde House Museum",
    "local-regulations": "Local Regulations / Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Tourism Vancouver",
    },
    {
      url: "https://www.vanartgallery.bc.ca/",
      label: "Vancouver Art Gallery Official Site",
    },
    {
      url: "https://moa.ubc.ca/",
      label: "Museum of Anthropology Official Site",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // ref you use in the corresponding <h2> or <p> section. Coordinates go in `lat`/`lng`.
  const locations = [
    {
      id: "section-1",
      lat: 49.28288,
      lng: -123.12046,
      popupText: "Vancouver Art Gallery",
    },
    {
      id: "section-2",
      lat: 49.26937,
      lng: -123.25960,
      popupText: "Museum of Anthropology",
    },
    {
      id: "section-3",
      lat: 49.27325,
      lng: -123.10377,
      popupText: "Science World",
    },
    {
      id: "section-4",
      lat: 49.27233,
      lng: -123.13967,
      popupText: "Museum of Vancouver",
    },
    {
      id: "section-5",
      lat: 49.27751,
      lng: -123.14726,
      popupText: "Vancouver Maritime Museum",
    },
    {
      id: "section-6",
      lat: 49.28476,
      lng: -123.11948,
      popupText: "Bill Reid Gallery",
    },
    {
      id: "section-7",
      lat: 49.26361,
      lng: -123.25139,
      popupText: "Beaty Biodiversity Museum",
    },
    {
      id: "section-8",
      lat: 49.27621,
      lng: -123.14440,
      popupText: "H.R. MacMillan Space Centre",
    },
    {
      id: "section-9",
      lat: 49.28224,
      lng: -123.09822,
      popupText: "Vancouver Police Museum",
    },
    {
      id: "section-10",
      lat: 49.28687,
      lng: -123.13215,
      popupText: "Roedde House Museum",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Museums in Vancouver: A Practical Guide</title>
        <meta
          name="description"
          content="Discover Vancouver's top 10 museums with practical info on tickets, hours, exhibits, local tips, and more."
        />
        <meta
          name="keywords"
          content="vancouver museums, top museums, art gallery, science world, MOA, museum of vancouver"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-museums-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Museums in Vancouver: A Practical Guide"
        />
        <meta
          property="og:description"
          content="Explore the city's best museums - from art and history to science and indigenous culture. Get tips on tickets, free days, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-museums-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-museums-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Museums in Vancouver: A Practical Guide"
        />
        <meta
          name="twitter:description"
          content="Explore the city's best museums - from art and history to science and indigenous culture. Get tips on tickets, free days, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-museums-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Exterior of one of Vancouver's museums"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver boasts a diverse museum scene, offering immersive
              experiences in art, history, science, and Indigenous culture. From
              downtown galleries to university campuses, these museums provide
              deeper insights into the city’s rich heritage, creative spirit,
              and scientific discoveries. Whether you’re a family looking for
              interactive fun or a seasoned culture vulture, you’ll find a
              museum that suits your interests and budget. Below is a practical
              guide to 10 of the city’s best museums, complete with addresses,
              coordinates, ticket info, and insider tips.
            </p>

            {/* SECTION 1: Vancouver Art Gallery */}
            <h2 id="section-1">Vancouver Art Gallery</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver Art Gallery</strong>{" "}
              <Marker
                coordY={49.28288}
                coordX={-123.12046}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              The Vancouver Art Gallery (VAG) is the largest art museum in
              Western Canada. Located in a historic courthouse building downtown,
              it houses over 10,000 artworks, including an extensive collection
              of pieces by British Columbian artists like Emily Carr. From
              permanent displays to rotating international exhibits, VAG offers
              an enriching experience for art enthusiasts.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 750 Hornby Street, Vancouver, BC
              </li>
              <li>
                <strong>Hours:</strong> Wed–Mon 10am–5pm (Fri until 8pm), closed
                Tuesdays
              </li>
              <li>
                <strong>Admission:</strong> Adults ~CA$35, discounts for seniors
                &amp; students, free for teens (13–18), and free for all on the
                first Friday of each month from 4–8pm
              </li>
            </ul>

            {/* SECTION 2: Museum of Anthropology */}
            <h2 id="section-2">Museum of Anthropology (MOA)</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Museum of Anthropology</strong>{" "}
              <Marker
                coordY={49.26937}
                coordX={-123.25960}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Situated on the University of British Columbia campus, the Museum
              of Anthropology showcases an impressive collection of Northwest
              Coast First Nations art, as well as artifacts from around the
              world. Highlights include towering totem poles in the Great Hall,
              contemporary Indigenous artworks, and Bill Reid’s iconic sculpture
              “The Raven and the First Men.” MOA provides an immersive
              exploration of Indigenous cultures and global anthropology.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 6393 NW Marine Drive, Vancouver, BC
              </li>
              <li>
                <strong>Hours:</strong> Tue–Sun 10am–5pm, closed Mondays
              </li>
              <li>
                <strong>Admission:</strong> Adults ~CA$25, youth ~CA$10, free for
                Indigenous peoples and UBC students, half-price on Thursday
                evenings after 5pm
              </li>
            </ul>

            {/* SECTION 3: Science World */}
            <h2 id="section-3">Science World</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Science World</strong>{" "}
              <Marker
                coordY={49.27325}
                coordX={-123.10377}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Housed in an iconic geodesic dome, Science World is a premier
              family destination for hands-on exhibits, live science
              demonstrations, and interactive galleries. Children and adults
              alike can explore physics, biology, and technology through
              engaging displays. The OMNIMAX Theatre (currently undergoing
              renovations) features giant-screen films for an immersive
              educational experience.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1455 Quebec Street, Vancouver, BC
              </li>
              <li>
                <strong>Hours:</strong> Daily 10am–5pm
              </li>
              <li>
                <strong>Admission:</strong> Adults ~CA$35, discounts for seniors
                &amp; youth, add-on for OMNIMAX films
              </li>
            </ul>

            {/* SECTION 4: Museum of Vancouver */}
            <h2 id="section-4">Museum of Vancouver</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Museum of Vancouver</strong>{" "}
              <Marker
                coordY={49.27233}
                coordX={-123.13967}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              The Museum of Vancouver delves into the city’s past and present,
              revealing its evolution from a small coastal settlement to a
              bustling, multicultural hub. Interactive exhibits showcase
              artifacts, neon signs, and historical sets from different eras,
              providing a colorful window into Vancouver’s cultural and social
              transformations.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1100 Chestnut Street, Vancouver, BC
              </li>
              <li>
                <strong>Hours:</strong> Daily 10am–5pm
              </li>
              <li>
                <strong>Admission:</strong> Adults ~CA$22 (check for promotions),
                free for Indigenous peoples
              </li>
            </ul>

            {/* SECTION 5: Vancouver Maritime Museum */}
            <h2 id="section-5">Vancouver Maritime Museum</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver Maritime Museum</strong>{" "}
              <Marker
                coordY={49.27751}
                coordX={-123.14726}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Located in Vanier Park, the Vancouver Maritime Museum celebrates
              the maritime history of the Pacific Northwest and the Arctic. Its
              highlight is the St. Roch, a historic RCMP vessel that navigated
              the Northwest Passage. Exhibits also include ship models,
              hands-on children’s areas, and rotating features on seafaring
              traditions.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1905 Ogden Avenue, Vancouver, BC
              </li>
              <li>
                <strong>Hours:</strong> Tue–Sun 10am–5pm
              </li>
              <li>
                <strong>Admission:</strong> Adults ~CA$18.50, discounts for youth
                &amp; seniors, free for children 5 and under
              </li>
            </ul>

            {/* SECTION 6: Bill Reid Gallery */}
            <h2 id="section-6">Bill Reid Gallery of Northwest Coast Art</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Bill Reid Gallery</strong>{" "}
              <Marker
                coordY={49.28476}
                coordX={-123.11948}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              The Bill Reid Gallery, nestled in downtown Vancouver, is Canada’s
              only public gallery dedicated to contemporary Indigenous Northwest
              Coast art. Named after renowned Haida artist Bill Reid, the gallery
              exhibits his masterful carvings, jewelry, and sculptures, along
              with works from emerging Indigenous artists that celebrate and
              preserve cultural heritage.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 639 Hornby Street, Vancouver, BC
              </li>
              <li>
                <strong>Hours:</strong> Tue–Sat 10am–5pm (closed Sun–Mon, except
                summers)
              </li>
              <li>
                <strong>Admission:</strong> Adults ~CA$13, seniors/students
                ~CA$10, free for Indigenous peoples, free on the first Friday of
                each month (2–5pm)
              </li>
            </ul>

            {/* SECTION 7: Beaty Biodiversity Museum */}
            <h2 id="section-7">Beaty Biodiversity Museum</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Beaty Biodiversity Museum</strong>{" "}
              <Marker
                coordY={49.26361}
                coordX={-123.25139}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              This natural history museum at UBC is renowned for its 25-meter
              blue whale skeleton suspended in the lobby. With over 2 million
              specimens, the museum highlights the region’s diverse flora and
              fauna, from fossils and insects to marine life. It’s a more
              academic, specimen-driven experience, offering insightful glimpses
              into biodiversity research.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 2212 Main Mall, Vancouver, BC
              </li>
              <li>
                <strong>Hours:</strong> Tue–Sun 10am–5pm, closed Mondays
              </li>
              <li>
                <strong>Admission:</strong> Adults ~CA$18, seniors/students
                ~CA$15, children under 5 free
              </li>
            </ul>

            {/* SECTION 8: H.R. MacMillan Space Centre */}
            <h2 id="section-8">H.R. MacMillan Space Centre</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>H.R. MacMillan Space Centre</strong>{" "}
              <Marker
                coordY={49.27621}
                coordX={-123.14440}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Often referred to as the Planetarium, the H.R. MacMillan Space
              Centre offers immersive shows in the Star Theatre, live
              demonstrations, and a cosmic courtyard of interactive exhibits. In
              the evenings, visitors can gaze at the night sky through the on-site
              observatory telescope (weather permitting). Great for space lovers
              of all ages.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1100 Chestnut Street, Vancouver, BC
              </li>
              <li>
                <strong>Hours:</strong> Daytime and evening schedules vary,
                closed some weekdays
              </li>
              <li>
                <strong>Admission:</strong> Adults ~CA$24, includes a planetarium
                show and gallery access
              </li>
            </ul>

            {/* SECTION 9: Vancouver Police Museum */}
            <h2 id="section-9">Vancouver Police Museum & Archives</h2>
            <p
              ref={(el) => setSpotRef("section-9", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver Police Museum</strong>{" "}
              <Marker
                coordY={49.28224}
                coordX={-123.09822}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Tucked away in a former Coroner’s Court building, the Vancouver
              Police Museum offers a unique look at crime history, forensics, and
              law enforcement. Visitors can explore the old morgue and autopsy
              rooms, see confiscated weapons, and learn about some of the city’s
              most infamous cases. Note: this museum is not wheelchair accessible
              due to stairs.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 240 E. Cordova Street, Vancouver, BC
              </li>
              <li>
                <strong>Hours:</strong> Thu–Sat 10am–5pm, Sun 12pm–5pm
              </li>
              <li>
                <strong>Admission:</strong> Adults ~CA$13.50, seniors/students
                ~CA$12, family packs available
              </li>
            </ul>

            {/* SECTION 10: Roedde House Museum */}
            <h2 id="section-10">Roedde House Museum</h2>
            <p
              ref={(el) => setSpotRef("section-10", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Roedde House Museum</strong>{" "}
              <Marker
                coordY={49.28687}
                coordX={-123.13215}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              This Victorian-era house museum in the West End provides a glimpse
              into family life in early Vancouver. Built in 1893 for the Roedde
              family, the home is furnished with period pieces and offers guided
              tours that recount the city’s late 19th-century social history. A
              charming and lesser-known gem.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1415 Barclay Street, Vancouver, BC
              </li>
              <li>
                <strong>Hours:</strong> Wed, Thu, Fri &amp; Sun 1pm–4pm
              </li>
              <li>
                <strong>Admission:</strong> Adults ~CA$10, children 5 &amp; under
                free
              </li>
            </ul>

            {/* SECTION: Local Regulations / Best Practices */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              Vancouver’s museums are visitor-friendly, but keep these tips in
              mind:
            </p>
            <ul>
              <li>
                <strong>COVID-19 Measures:</strong> Currently, there are no
                strict mandates. Masks are recommended in crowded areas; check
                official websites for updates.
              </li>
              <li>
                <strong>Photography:</strong> Most museums allow non-flash
                photography, unless otherwise stated. Flash or tripods are
                usually prohibited to protect artifacts.
              </li>
              <li>
                <strong>Bag Checks:</strong> Large bags may need to be checked.
              </li>
              <li>
                <strong>Transit vs. Driving:</strong> Public transport is often
                easier due to limited parking, especially downtown. UBC museums
                have bus routes directly from downtown.
              </li>
              <li>
                <strong>Accessibility:</strong> Most are wheelchair accessible,
                except for historic buildings like the Police Museum and Roedde
                House (partially). Always confirm if you have specific mobility
                needs.
              </li>
              <li>
                <strong>Free Days:</strong> Many museums offer free or discounted
                admission on specific days or times (e.g., VAG on first Friday
                evening). Check schedules to plan accordingly.
              </li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Which museum is best for kids?</h3>
              <p>
                Science World is the top choice for families, offering
                interactive exhibits and hands-on activities that keep children
                engaged for hours. The Space Centre is also fun for budding
                astronomers, while museums like the Maritime Museum have
                child-friendly sections, too.
              </p>

              <h3>2. Do any museums offer free admission?</h3>
              <p>
                Yes. Vancouver Art Gallery is free on the first Friday of each
                month (4–8pm). Bill Reid Gallery also offers free admission on
                the first Friday (2–5pm). Teens 13–18 get in free at VAG anytime,
                and many institutions offer free entry for Indigenous peoples.
              </p>

              <h3>3. How can I visit several museums in one day?</h3>
              <p>
                Plan by location. For instance, Vanier Park has MOV, the Maritime
                Museum, and the Space Centre steps away from each other. UBC has
                MOA and Beaty Museum close by. Downtown, you can easily walk from
                Vancouver Art Gallery to Bill Reid Gallery.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s museums offer countless opportunities to explore art,
              culture, science, and local history. Whether you’re drawn to the
              iconic geodesic dome of Science World, the world-class collections
              of the Vancouver Art Gallery, or the intimate halls of the Bill
              Reid Gallery, there’s a rich tapestry of experiences waiting.
              Remember to check each museum’s hours, plan around free or
              discounted days, and use public transit when possible. With this
              guide, you’re all set to discover the best museums Vancouver has
              to offer!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}